import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { NameserverGlue } from '../types/models/DomainName';
import type { ApiResponseEffectiveNameservers } from '../types/api/ApiResponseDomainName';

type EffectiveNameservers = Record<string, NameserverGlue | null>;

interface UseEffectiveNameserversReturn {
  nameservers: EffectiveNameservers | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useEffectiveNameservers = (id: number): UseEffectiveNameserversReturn => {
  const [nameservers, setNameservers] = useState<EffectiveNameservers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchEffectiveNameservers = useCallback(async () => {
    if (id <= 0) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/domain-names/${id}/nameservers/refresh`);

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseEffectiveNameservers;
      if (data.status === 'success' && data.data?.nameservers) {
        setNameservers(data.data.nameservers);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to resolve nameservers');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while resolving nameservers');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchEffectiveNameservers();
    }
  }, [auth.isAuthenticated, fetchEffectiveNameservers]);

  return { nameservers, loading, error, refetch: fetchEffectiveNameservers };
};
